// ChapterRegenButton — chapter-scoped regen entry for the dashboard ChapterCard.
//
// 19BJ Session 5: completes the embedded regen migration (10/10 views).
// Design doc: docs/architecture/regen-embedded-design.md §2.6.
//
// Unlike ItemActions (single-item, Quick-or-Deep auto-route), a chapter
// regen operates on an entire stage across one chapter — e.g. "regenerate
// all MCQs in chapter X". That scope always exceeds the 30s Quick budget,
// so it is forced onto the Deep path (useUnifiedRegen { forceDeep: true }).
//
// UX: ⟳ button in the ChapterCard header → popover with a mode toggle
// (replace / add) and the chapter's enabled stages. Picking a stage fires
// the regen; results surface via the same RegenPreview inline overlay +
// course-shell polling bridge used by ItemActionMenu.
//
// Audit B1.3 boundary: this is "I want different/more content for this
// chapter" (active regen), distinct from any per-item flag/repair.

import { useState, useCallback, useRef, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { useUnifiedRegen } from '../lib/hooks/use-unified-regen.js';
import { useStore } from '../lib/store/index.js';
import { useT } from '../i18n/index.js';
import { RegenPreview } from './regen-preview.js';
import type { StageOption } from '../lib/regen-utils.js';
import type { RegenAction } from '@examforge/shared/schemas';

export interface ChapterRegenButtonProps {
  courseId: string;
  chapterId: string;
  /** Stages enabled for this course (from getEnabledStages(draft.enabledComponents)). */
  enabledStages: StageOption[];
  /** Called after regen is accepted and merged (usually refreshProgress). */
  onRegenComplete?: () => void;
}

/** Default number of items to add in 'add' mode. */
const ADD_COUNT = 3;

export function ChapterRegenButton({
  courseId,
  chapterId,
  enabledStages,
  onRegenComplete,
}: ChapterRegenButtonProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'replace' | 'add'>('replace');
  // P2-5 (DEBT-19BO+-LEGACY-REGEN-BACKDOOR · 2026-06-28): optional user hint.
  // Migrated from outline-chapter-row's inline regen form (50-char input).
  // Reaches the regen prompt: replace→userFeedback / add→userPromptHint
  // (regen-action.ts field names differ by design).
  const [hint, setHint] = useState('');
  const wrapRef = useRef<HTMLSpanElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const t = useT();

  const regen = useUnifiedRegen(courseId);
  // Extract the two stable sub-references used by the poll bridge effect below
  // so the dep array can name them directly (avoids depending on the `regen`
  // object, which is recreated every render). (react-hooks/exhaustive-deps · Part E 第四波)
  const { path: regenPath, loadPreviewFromRegenSlice } = regen;

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── Deep path polling bridge ──────────────────────────────────────────────
  // Mirrors ItemActionMenu: when the global regenSlice reaches 'confirm' on the
  // Deep path, copy its pending items into local preview state so RegenPreview
  // can render them.
  const regenStatus = useStore((s) => s.regenStatus);
  const regenPendingItems = useStore((s) => s.regenPendingItems);
  const regenBeforeSnapshot = useStore((s) => s.regenBeforeSnapshot);
  const regenDiffSummary = useStore((s) => s.regenDiffSummary);
  const regenSliceId = useStore((s) => s.regenId);

  useEffect(() => {
    if (
      regenPath === 'deep' &&
      regenStatus === 'confirm' &&
      regenPendingItems &&
      regenSliceId
    ) {
      loadPreviewFromRegenSlice(
        regenPendingItems,
        regenBeforeSnapshot ?? [],
        regenDiffSummary ?? null,
        regenSliceId,
      );
    }
  }, [regenPath, regenStatus, regenPendingItems, regenBeforeSnapshot,
      regenDiffSummary, regenSliceId, loadPreviewFromRegenSlice]);

  // ── Action handler ────────────────────────────────────────────────────────

  const handleStage = useCallback((stageId: string) => {
    setOpen(false);
    const trimmed = hint.trim();
    // Chapter-scoped: scope.caseIds = [chapterId] → runRegenCore runs the
    // stage per-chapter (singleChapter = scope.caseIds[0]).
    // Hint field name follows RegenAction's discriminated union: replace
    // variant uses userFeedback (regen-action.ts:79), add variant uses
    // userPromptHint (regen-action.ts:97). Both are optional; omit when empty.
    const action = (mode === 'replace'
      ? {
          kind: 'replace' as const,
          scope: { stage: stageId, caseIds: [chapterId] },
          ...(trimmed ? { userFeedback: trimmed } : {}),
        }
      : {
          kind: 'add' as const,
          scope: { stage: stageId, caseIds: [chapterId] },
          count: ADD_COUNT,
          ...(trimmed ? { userPromptHint: trimmed } : {}),
        }
    ) as unknown as RegenAction;
    setHint('');
    void regen.trigger(action, stageId, chapterId, { forceDeep: true });
  }, [mode, hint, chapterId, regen]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const isActive = open || regen.status === 'loading' || regen.status === 'preview';
  const disabled = enabledStages.length === 0;

  return (
    <span className="chapter-regen-btn-wrap" ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        className={`chapter-regen-btn${isActive ? ' is-active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        title={disabled ? t('chapterRegen.disabledTitle') : t('chapterRegen.title')}
        aria-label={t('chapterRegen.ariaLabel')}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <RefreshCw size={12} aria-hidden="true" />
      </button>

      {open && !disabled && (
        <div className="chapter-regen-dropdown" role="menu">
          <div className="chapter-regen-mode" role="group" aria-label={t('chapterRegen.modeGroup')}>
            <button
              type="button"
              className={`chapter-regen-mode-btn${mode === 'replace' ? ' is-active' : ''}`}
              onClick={() => setMode('replace')}
              aria-pressed={mode === 'replace'}
            >
              {t('regen.replaceLabel')}
            </button>
            <button
              type="button"
              className={`chapter-regen-mode-btn${mode === 'add' ? ' is-active' : ''}`}
              onClick={() => setMode('add')}
              aria-pressed={mode === 'add'}
            >
              {t('regen.addLabel')}
            </button>
          </div>
          {/* Optional hint — reaches the regen prompt (replace→userFeedback / add→userPromptHint). */}
          <input
            type="text"
            className="chapter-regen-hint-input"
            placeholder={t('chapterRegen.hintPlaceholder')}
            value={hint}
            onChange={(e) => setHint(e.target.value.slice(0, 50))}
            onClick={(e) => e.stopPropagation()}
            maxLength={50}
            aria-label={t('chapterRegen.hintPlaceholder')}
          />
          <ul className="chapter-regen-stage-list">
            {enabledStages.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className="chapter-regen-stage-item"
                  role="menuitem"
                  onClick={() => handleStage(s.id)}
                  disabled={regen.status === 'loading'}
                >
                  <RefreshCw size={11} aria-hidden="true" />
                  <span>{s.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* RegenPreview inline overlay — shown when regen returns preview items. */}
      {regen.status === 'preview' && regen.previewItems.length > 0 && (
        <RegenPreview
          newItems={regen.previewItems}
          originalItems={regen.originalItems}
          stage={regen.stage}
          action={regen.action}
          path={regen.path}
          cost={regen.cost}
          triggerRef={btnRef}
          onAccept={(selected) => {
            void regen.accept(selected).then(() => onRegenComplete?.());
          }}
          onReject={() => regen.reject()}
        />
      )}

      {/* Loading indicator */}
      {regen.status === 'loading' && (
        <span className="chapter-regen-loading" aria-label={t('chapterRegen.loading')}>
          <RefreshCw size={11} className="chapter-regen-spinner" aria-hidden="true" />
        </span>
      )}
    </span>
  );
}
